import { z } from 'zod';
import {
  permissionBundles,
  requiresPersonLink,
  type Permission,
  type PermissionBundle,
} from './permissions.js';
import { normalizeName } from './people.js';
import type { SectionPermission } from './sections.js';

/** Invitations expire 72 hours after creation (specification section 3). */
export const invitationLifetimeHours = 72;

/**
 * The link an invitee opens, in the e-mail or handed over by the inviter. The token sits in the
 * fragment, which browsers never send to a server, so it stays out of access logs and referrer headers.
 */
export function invitationLink(origin: string, token: string): string {
  return `${origin}/invite#token=${encodeURIComponent(token)}`;
}

const invitedNameSchema = z.string().transform(normalizeName).pipe(z.string().min(1).max(200));

/** At most one of each role; together they are granted in the invitation's single scope. */
const invitedBundlesSchema = z
  .array(z.enum(permissionBundles))
  .min(1)
  .max(permissionBundles.length)
  .refine((bundles) => new Set(bundles).size === bundles.length);

export const createInvitationRequestSchema = z
  .strictObject({
    recipientEmail: z.email().max(254),
    /**
     * Roles granted together on acceptance, for example Club administrator, Coach and Secretary. A
     * guardian or athlete role acts for one person and is always offered on its own.
     */
    permissionBundles: invitedBundlesSchema,
    departmentId: z.uuid().nullable().default(null),
    /** Approved person link; guardian and athlete invitations need it or, for a guardian, `newGuardian`. */
    personId: z.uuid().nullable().default(null),
    /**
     * A parent who has no person record yet. The server creates it with the invitation's e-mail, so
     * the parent can be invited before any child is linked; the role alone shows no child.
     */
    newGuardian: z
      .strictObject({ givenName: invitedNameSchema, familyName: invitedNameSchema })
      .optional(),
  })
  .refine((request) => request.newGuardian === undefined || request.personId === null, {
    path: ['newGuardian'],
  })
  .refine(
    (request) =>
      request.permissionBundles.length === 1 || !request.permissionBundles.some(requiresPersonLink),
    { path: ['permissionBundles'] },
  )
  .refine(
    (request) =>
      request.newGuardian === undefined ||
      (request.permissionBundles.length === 1 && request.permissionBundles[0] === 'guardian'),
    { path: ['newGuardian'] },
  );
export type CreateInvitationRequest = z.infer<typeof createInvitationRequestSchema>;

/** One deletion request carries at most this many invitations; the client sends larger selections in parts. */
export const maximumInvitationsPerDeletion = 100;

/** Finished invitations to delete from the history; a waiting invitation is withdrawn first. */
export const deleteInvitationsRequestSchema = z.strictObject({
  invitationIds: z
    .array(z.uuid())
    .min(1)
    .max(maximumInvitationsPerDeletion)
    .refine((identifiers) => new Set(identifiers).size === identifiers.length),
});
export type DeleteInvitationsRequest = z.infer<typeof deleteInvitationsRequestSchema>;

export interface DeletedInvitationsResponse {
  deletedCount: number;
}

/**
 * Roles added to an existing member. Guardian and athlete roles are not assignable here: they act
 * for a person and arrive only through an invitation that carries an approved person link.
 */
export const assignRoleRequestSchema = z.strictObject({
  permissionBundle: z.enum(permissionBundles),
  departmentId: z.uuid().nullable().default(null),
  validUntil: z.iso.datetime().nullable().default(null),
  reason: z.string().trim().max(500).optional(),
});
export type AssignRoleRequest = z.infer<typeof assignRoleRequestSchema>;

/** Change only the end of an existing assignment; scope and start remain historical. */
export const updateRoleValidityRequestSchema = z.strictObject({
  validUntil: z.iso.datetime().nullable(),
  reason: z.string().trim().max(500).optional(),
});
export type UpdateRoleValidityRequest = z.infer<typeof updateRoleValidityRequestSchema>;

export const revokeRoleRequestSchema = z.strictObject({
  reason: z.string().trim().max(500).optional(),
});
export type RevokeRoleRequest = z.infer<typeof revokeRoleRequestSchema>;

/** Suspending or reactivating a membership; the reason goes to the audit record only. */
export const changeMemberStatusRequestSchema = z.strictObject({
  reason: z.string().trim().max(500).optional(),
});
export type ChangeMemberStatusRequest = z.infer<typeof changeMemberStatusRequestSchema>;

export const acceptInvitationRequestSchema = z.strictObject({
  token: z.string().min(20).max(200),
});
export type AcceptInvitationRequest = z.infer<typeof acceptInvitationRequestSchema>;

export const selectOrganizationRequestSchema = z.strictObject({ organizationId: z.uuid() });
export type SelectOrganizationRequest = z.infer<typeof selectOrganizationRequestSchema>;

export const updateLocaleRequestSchema = z.strictObject({ locale: z.enum(['en', 'hu', 'ro']) });
export type UpdateLocaleRequest = z.infer<typeof updateLocaleRequestSchema>;

/** Result of `GET /auth/session`; the CSRF token goes in `X-CSRF-Token` on every state change. */
export interface SessionStatusResponse {
  authenticated: boolean;
  csrfToken?: string;
}

/** Result of switching organization or accepting an invitation; the session identifier changed. */
export interface SessionChangedResponse {
  activeOrganizationId: string;
  csrfToken: string;
}

export interface MembershipSummary {
  organizationId: string;
  organizationName: string;
  bundles: PermissionBundle[];
}

export interface MembershipsResponse {
  items: MembershipSummary[];
}

export interface MeResponse {
  principalId: string;
  activeOrganizationId: string | null;
  /** Locale chosen for the active organization, if any. */
  locale: string | null;
  memberships: MembershipSummary[];
  /** Section permissions in the active organization; empty until one is selected. */
  sections: SectionPermission[];
  permissions: Permission[];
  /**
   * True when the person holds a staff role here but signed in without multi-factor
   * authentication; sections and permissions are then empty until they sign in again with it.
   */
  mfaRequired: boolean;
  /**
   * Milliseconds since the epoch at which the active club's logo last changed, or null when it has
   * none. Visible to every member; it is appended to the logo address so a new logo replaces a cached one.
   */
  logoVersion: number | null;
}

export interface InvitationView {
  id: string;
  recipientEmail: string;
  permissionBundles: PermissionBundle[];
  departmentId: string | null;
  personId: string | null;
  status: 'pending' | 'accepted' | 'revoked' | 'expired';
  expiresAt: string;
  /** When the mail server accepted the invitation e-mail; null when it was not sent. */
  emailSentAt: string | null;
  version: number;
}

/**
 * What happened to the invitation e-mail. `no_sender`: the club has not set a sender address in
 * Settings. `unavailable`: the server has no mail server or public address configured. `failed`: the
 * mail server refused or could not be reached. In every case but `sent` the inviter hands the link over.
 */
export const invitationEmailDeliveries = ['sent', 'no_sender', 'unavailable', 'failed'] as const;
export type InvitationEmailDelivery = (typeof invitationEmailDeliveries)[number];

export interface CreatedInvitationResponse extends InvitationView {
  /**
   * One-time acceptance token. Only its hash is stored, so it is shown once to the inviter and
   * cannot be recovered later.
   */
  token: string;
  emailDelivery: InvitationEmailDelivery;
}

export interface RoleAssignmentView {
  id: string;
  permissionBundle: PermissionBundle;
  departmentId: string | null;
  validFrom: string;
  validUntil: string | null;
  revokedAt: string | null;
  version: number;
}

export interface MemberView {
  organizationUserId: string;
  status: string;
  /** Membership row version; send it in `If-Match` when suspending or reactivating. */
  version: number;
  /** True for the caller's own membership; club-wide admins may add explicit operational roles. */
  isSelf: boolean;
  personLinked: boolean;
  /** Member's name, or null when the club knows none yet. */
  displayName: string | null;
  /** Member's e-mail address, or null when the club knows none yet. */
  email: string | null;
  assignments: RoleAssignmentView[];
}

/** Result of suspending or reactivating a membership. */
export interface MemberStatusView {
  organizationUserId: string;
  status: string;
  version: number;
}

export interface PageResponse<Item> {
  items: Item[];
  nextCursor: string | null;
}
